/**
 * Действия над полем прямо в попапе поля на /map (AK-772, решение founder
 * 28.08 «выверка полей с карты», этап 1).
 *
 * Сценарий: агроном идёт по карте и глазами выверяет поля — вот это названо
 * не так, этого поля уже нет, у этого контур врёт. Раньше на каждое такое
 * наблюдение приходилось уходить в карточку поля и возвращаться на карту,
 * теряя место, где он остановился. Теперь три действия — на месте:
 *
 *   · «Переименовать» — тот же диалог, что в карточке поля
 *     (`RenameFieldDialog`, PATCH /fields/{id}); второго способа
 *     переименовать поле в системе не заводим;
 *   · «Архивировать»  — тот же диалог с ОБЯЗАТЕЛЬНОЙ причиной, что у
 *     пакетной чистки списка (`FieldsArchiveDialog`, POST /fields/archive);
 *   · «Геометрия»     — переход в редактор контура карточки поля
 *     (`/fields/{id}?tab=geometry`). Полный редактор контура НА САМОЙ карте —
 *     этап 2, отдельной работой.
 *
 * Диалоги живут НЕ здесь, а на уровне страницы карты: попап MapLibre может
 * закрыться (клик по карте, соседнее поле) и унести портал с собой — диалог
 * посреди ввода причины исчезать не должен. Отсюда наружу уходят только
 * «человек попросил переименовать / заархивировать».
 *
 * Права — те же, что гейтят кнопки в карточке поля: «Переименовать» —
 * `PERM.fieldContentWrite` (PATCH поля, его ведёт и гл. агроном хозяйства,
 * AK-929), «Архивировать» — `PERM.fieldWrite` (структура хозяйства). Без
 * права действия не рисуются вовсе: попап — тесная
 * поверхность, ряд серых кнопок с тултипом «нет прав» тут читается как
 * поломка, а не как объяснение. «Геометрия» остаётся всегда — это навигация
 * на вкладку карточки, а не запись.
 */
import { Archive, Pencil, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCan, PERM } from '@/lib/auth/usePermissions';
import { isCapable } from '@/pages/structure/structureLogic';

/**
 * Плотность под попап: три кнопки должны уместиться в 300px максимум ширины
 * попапа в две строки, а не в три. Ступени из разрешённой шкалы отступов.
 */
const COMPACT = 'h-8 px-2 text-xs';

export interface FieldActionsBlockProps {
  fieldId: string;
  /** Номер/имя поля из MVT-свойств — им подписаны диалоги и итоги. */
  fieldName: string;
  onRename: () => void;
  onArchive: () => void;
}

/**
 * Ссылка «Геометрия» — карточка поля, вкладка «Геометрия» (см. card/tabs.ts).
 *
 * Открывается В НОВОЙ ВКЛАДКЕ (`target="_blank"`, review head-web по #978) —
 * ровно как соседняя «Открыть подробнее» в этом же попапе. Причина ровно та,
 * ради которой фича и делалась: уход С КАРТЫ стоит дорого (холодный старт
 * самой тяжёлой страницы, потерянные zoom/центр, активная область, спутниковый
 * слой, режим подписей), а сценарий «сверил контур — вернулся к выверке» этого
 * ухода не требует вовсе. Роутерный `Link` здесь не берём осознанно: у
 * маршрута `/fields/$fieldId` нет `validateSearch`, и ради одной кнопки
 * заводить схему query-параметров — это чужая зона риска (грабля числовых
 * флагов TanStack), а карта всё равно осталась бы позади.
 */
export function geometryHref(fieldId: string): string {
  return `/fields/${fieldId}?tab=geometry`;
}

export function FieldActionsBlock({
  fieldId,
  fieldName,
  onRename,
  onArchive,
}: FieldActionsBlockProps) {
  // AK-363: архив поля — структура хозяйства (`fieldWrite`). AK-929: имя —
  // содержимое поля (`fieldContentWrite`), его правит и гл. агроном хозяйства.
  const canWriteField = useCan(PERM.fieldWrite, 'u');
  const canEditContent = useCan(PERM.fieldContentWrite, 'u');
  const canRename = canEditContent && isCapable('renameField');

  return (
    <div
      className="flex flex-wrap gap-2 pt-2"
      data-testid="field-popover-actions"
      aria-label={`Действия над полем ${fieldName}`}
    >
      {canRename && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={COMPACT}
          data-testid="map-field-rename"
          onClick={onRename}
        >
          <Pencil className="mr-1 h-4 w-4" />
          Переименовать
        </Button>
      )}
      <Button
        asChild
        size="sm"
        variant="outline"
        className={COMPACT}
        data-testid="map-field-geometry"
      >
        <a href={geometryHref(fieldId)} target="_blank" rel="noopener">
          <Ruler className="mr-1 h-4 w-4" />
          Геометрия
        </a>
      </Button>
      {canWriteField && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={COMPACT}
          data-testid="map-field-archive"
          onClick={onArchive}
        >
          <Archive className="mr-1 h-4 w-4" />
          Архивировать
        </Button>
      )}
    </div>
  );
}
